import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, CheckCircle2, Sparkles } from "lucide-react";
import { BUSINESS, IMAGES } from "../lib/constants";

const LINES = ["Professional Carpet &", "Cleaning Services", "on the Gold Coast"];

export const HomeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <div aria-hidden="true" className="absolute -top-32 -right-40 w-[38rem] h-[38rem] rounded-full bg-brand-mist/70 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 sm:pt-20 sm:pb-24 grid lg:grid-cols-12 gap-14 items-center relative">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="inline-flex items-center gap-2 bg-white border border-brand-line/70 rounded-full px-4 py-2 shadow-sm mb-8"
            data-testid="hero-badge"
          >
            <Sparkles size={14} className="text-brand" />
            <span className="text-xs font-semibold tracking-[0.14em] uppercase text-brand">
              Gold Coast Local · Residential & Commercial
            </span>
          </motion.div>

          <h1 className="font-display font-medium text-5xl sm:text-6xl md:text-[5rem] leading-[1.04] tracking-tight text-brand-ink" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "font-serif italic font-medium text-brand" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ type: "spring", stiffness: 58, damping: 17, delay: 0.2 + i * 0.13 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-7 text-base sm:text-lg text-brand-muted leading-relaxed max-w-xl"
            data-testid="hero-subtext"
          >
            More than 7 years of hands-on cleaning experience and thousands of completed
            jobs. Professional carpet and cleaning services across the Gold Coast and
            selected surrounding areas — from carpets and couches to rugs, mattresses,
            tiles, commercial
            spaces and water-damaged areas, talk directly to Kay's Carpet Cleaning about what
            you need cleaned.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/get-estimate"
              data-testid="hero-estimate-button"
              className="group inline-flex items-center gap-2.5 bg-brand hover:bg-brand-dark text-white font-semibold px-7 py-4 rounded-full text-base shadow-[0_10px_30px_rgba(45,90,83,0.28)] transition-[background-color,transform] duration-300 hover:scale-[0.97]"
            >
              Get My Estimate
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={BUSINESS.phoneHref}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-2.5 bg-white border border-brand-line text-brand-ink font-semibold px-7 py-4 rounded-full text-base transition-[border-color,transform] duration-300 hover:border-brand hover:scale-[0.97]"
            >
              <Phone size={18} className="text-brand" />
              Call {BUSINESS.phoneDisplay}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
            data-testid="hero-trust-indicators"
          >
            {["7+ Years Experience", "Thousands of Jobs", "Gold Coast Local"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 text-sm font-medium text-brand-muted">
                <CheckCircle2 size={16} className="text-brand" />
                {t}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="overflow-hidden rounded-t-[10rem] rounded-b-[2rem] border border-brand-line/60 shadow-[0_30px_80px_rgba(45,90,83,0.18)]">
                <img
                  src={IMAGES.hero}
                  alt="Professionally cleaned bright Gold Coast living room carpet"
                  className="w-full h-[420px] sm:h-[540px] object-cover"
                  loading="eager"
                  data-testid="hero-image"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="absolute -bottom-6 -left-4 sm:-left-8 bg-white/85 backdrop-blur-xl border border-brand-line/70 rounded-2xl px-5 py-4 shadow-[0_16px_40px_rgba(19,28,26,0.12)] animate-float-slow"
              data-testid="hero-floating-badge"
            >
              <p className="font-display font-semibold text-2xl text-brand leading-none">7+</p>
              <p className="text-xs font-medium text-brand-muted mt-1 tracking-wide">Years of hands-on<br />cleaning experience</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
